/**
 * Wallpaper presets painted behind the window card.
 */
import { clamp } from './format'

export type WallpaperKind = 'mesh' | 'aurora' | 'obsidian' | 'solid'

export interface Wallpaper {
  id: string
  name: string
  kind: WallpaperKind
  /** Base fill color. */
  base: string
  /** Gradient stops used by `mesh` / `aurora` painters. */
  colors: string[]
  /** CSS used for the swatch preview in the UI. */
  swatch: string
  hint: string
  /** True for the user's custom brand color. */
  custom?: boolean
}

export const DEFAULT_WALLPAPER_ID = 'mesh-dusk'
export const CUSTOM_WALLPAPER_ID = 'brand-solid'

export const WALLPAPERS: readonly Wallpaper[] = [
  {
    id: 'mesh-dusk',
    name: 'Mesh Dusk',
    kind: 'mesh',
    base: '#120a1f',
    colors: ['#7c3aed', '#db2777', '#f59e0b', '#0ea5e9'],
    hint: 'Soft mesh gradient, works with any product UI',
    swatch: 'radial-gradient(at 20% 20%, #7c3aed 0%, transparent 55%), radial-gradient(at 80% 25%, #db2777 0%, transparent 55%), radial-gradient(at 60% 80%, #0ea5e9 0%, transparent 55%), #120a1f',
  },
  {
    id: 'aurora-neon',
    name: 'Neon Aurora',
    kind: 'aurora',
    base: '#04070f',
    colors: ['#22d3ee', '#a855f7', '#34d399'],
    hint: 'Dark, high-contrast for neon product shots',
    swatch: 'radial-gradient(at 25% 15%, #22d3ee 0%, transparent 55%), radial-gradient(at 75% 30%, #a855f7 0%, transparent 55%), radial-gradient(at 50% 90%, #34d399 0%, transparent 60%), #04070f',
  },
  {
    id: 'obsidian',
    name: 'Dark Obsidian',
    kind: 'obsidian',
    base: '#09090b',
    colors: ['#18181b', '#27272a'],
    hint: 'Editorial black — the safest default',
    swatch: 'radial-gradient(at 50% 35%, #27272a 0%, #09090b 70%)',
  },
  {
    id: 'midnight',
    name: 'Midnight Blue',
    kind: 'mesh',
    base: '#050b1a',
    colors: ['#1d4ed8', '#0ea5e9', '#6366f1'],
    hint: 'Cool, trustworthy SaaS feel',
    swatch: 'radial-gradient(at 20% 25%, #1d4ed8 0%, transparent 55%), radial-gradient(at 80% 20%, #0ea5e9 0%, transparent 55%), radial-gradient(at 55% 85%, #6366f1 0%, transparent 55%), #050b1a',
  },
  {
    id: 'ember',
    name: 'Warm Ember',
    kind: 'mesh',
    base: '#1a0a05',
    colors: ['#f97316', '#dc2626', '#facc15'],
    hint: 'Energetic, great for launch videos',
    swatch: 'radial-gradient(at 22% 22%, #f97316 0%, transparent 55%), radial-gradient(at 78% 28%, #dc2626 0%, transparent 55%), radial-gradient(at 55% 82%, #facc15 0%, transparent 60%), #1a0a05',
  },
  {
    id: 'paper',
    name: 'Soft Paper',
    kind: 'mesh',
    base: '#f4f4f5',
    colors: ['#e4e4e7', '#d4d4d8', '#a1a1aa'],
    hint: 'Light mode canvas for bright products',
    swatch: 'radial-gradient(at 20% 20%, #e4e4e7 0%, transparent 55%), radial-gradient(at 80% 25%, #d4d4d8 0%, transparent 55%), #f4f4f5',
  },
]

/** `#abc` -> `#aabbcc`; anything else becomes null. */
export function normalizeHex(input: string): string | null {
  const value = input.trim().replace(/^#/, '')
  if (/^[0-9a-fA-F]{3}$/.test(value)) {
    return `#${value
      .split('')
      .map((char) => char + char)
      .join('')
      .toLowerCase()}`
  }
  if (/^[0-9a-fA-F]{6}$/.test(value)) return `#${value.toLowerCase()}`
  return null
}

export function isValidHex(input: string): boolean {
  return normalizeHex(input) !== null
}

/** Build the "Brand Solid" wallpaper from a user supplied hex. */
export function customWallpaper(hex: string): Wallpaper {
  const color = normalizeHex(hex) ?? '#0f172a'
  return {
    id: CUSTOM_WALLPAPER_ID,
    name: 'Brand Solid',
    kind: 'solid',
    base: color,
    colors: [color],
    hint: `Flat ${color} — your brand color, pro`,
    swatch: color,
    custom: true,
  }
}

export function getWallpaper(id: string): Wallpaper {
  if (id === CUSTOM_WALLPAPER_ID) {
    return WALLPAPERS.find((wallpaper) => wallpaper.id === DEFAULT_WALLPAPER_ID) as Wallpaper
  }
  return WALLPAPERS.find((wallpaper) => wallpaper.id === id) ?? (WALLPAPERS[0] as Wallpaper)
}

/** Resolve a wallpaper id + custom color into a concrete wallpaper. */
export function resolveWallpaper(id: string, customColor: string): Wallpaper {
  if (id === CUSTOM_WALLPAPER_ID) return customWallpaper(customColor)
  return getWallpaper(id)
}

/** Mix two hex colors; used to derive vignette tints. */
export function mixHex(a: string, b: string, amount: number): string {
  const first = hexToRgb(a)
  const second = hexToRgb(b)
  if (!first || !second) return a
  const t = clamp(amount, 0, 1)
  const channel = (index: 0 | 1 | 2) => Math.round(first[index] + (second[index] - first[index]) * t)
  return rgbToHex(channel(0), channel(1), channel(2))
}

export function hexToRgb(hex: string): [number, number, number] | null {
  const normalized = normalizeHex(hex)
  if (!normalized) return null
  const value = normalized.slice(1)
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ]
}

export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (channel: number) =>
    Math.round(clamp(channel, 0, 255))
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/** Relative luminance, used to pick readable watermark ink. */
export function isLightColor(hex: string): boolean {
  const rgb = hexToRgb(hex)
  if (!rgb) return false
  const [r, g, b] = rgb.map((channel) => {
    const c = channel / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.5
}
